/** @jsxImportSource react */
import { useEffect, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, Chip, Divider, IconButton, MenuItem, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { formsAPI, recordsAPI, whatsappAPI } from '../../api/crm'
import { useEntityTypes, useTenantKey } from '../../hooks/useCrmQueries'
import { allFields } from '../../components/records/fieldValues'
import PageHeader from '../../components/common/PageHeader'
import Loading from '../../components/common/Loading'
import { useNotify } from '../../components/common/NotificationProvider'
import { entityLabel, entityLabelPlural } from '../../utils/helpers'
import { getErrorMessage } from '../../utils/apiErrors'

const Copy = ({ label, value }) => {
  const notify = useNotify()
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      <TextField size="small" label={label} value={value} fullWidth InputProps={{ readOnly: true }} />
      <Tooltip title="Copy"><IconButton onClick={() => { navigator.clipboard?.writeText(value); notify('Copied') }}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
    </Stack>
  )
}

/** Fields of each record type, for the phone / name pickers. */
const useTypeFields = (forms) => {
  const tenantId = useTenantKey()
  return useQuery({
    queryKey: [tenantId, 'forms', 'fields-by-type', forms.map((f) => f.code).join()],
    queryFn: async () => Object.fromEntries(await Promise.all(forms.map(async (f) => [f.entityType, allFields(await formsAPI.published(f.code))]))),
    enabled: forms.length > 0,
  })
}

/** Admins: connect the workspace's WhatsApp Business number, and deal with messages from unknown numbers. */
const WhatsAppSettingsPage = () => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const { entityTypes, forms } = useEntityTypes()
  const typeFields = useTypeFields(forms)
  const account = useQuery({ queryKey: [tenantId, 'whatsapp', 'account'], queryFn: whatsappAPI.account })
  const a = account.data
  const [v, setV] = useState({ phoneNumberId: '', wabaId: '', accessToken: '', appSecret: '', defaultCountryCode: '91', contacts: [], createType: '' })
  useEffect(() => {
    if (a) setV({ phoneNumberId: a.phoneNumberId || '', wabaId: a.wabaId || '', accessToken: '', appSecret: '', defaultCountryCode: a.defaultCountryCode || '91',
      contacts: a.contacts || [], createType: a.createType || '' })
  }, [a])
  const refresh = () => queryClient.invalidateQueries({ queryKey: [tenantId, 'whatsapp'] })
  const save = useMutation({ mutationFn: () => whatsappAPI.connect({ ...v, createType: v.createType || null }), onSuccess: () => { refresh(); notify('WhatsApp saved') } })
  const disconnect = useMutation({ mutationFn: whatsappAPI.disconnect, onSuccess: () => { refresh(); notify('Disconnected') } })
  const phoneFields = (t) => (typeFields.data?.[t] || []).filter((f) => f.fieldType === 'PHONE')
  const nameFields = (t) => (typeFields.data?.[t] || []).filter((f) => f.fieldType === 'TEXT')
  const setContact = (i, patch) => setV({ ...v, contacts: v.contacts.map((c, j) => (j === i ? { ...c, ...patch } : c)) })

  if (account.isLoading) return <Loading />
  return (
    <Box sx={{ maxWidth: 860 }}>
      <PageHeader title="WhatsApp" subtitle="Message customers from their records with your WhatsApp Business number; replies land back on the record." />
      {a?.connected && a.lastError && <Alert severity="warning" sx={{ mb: 2 }}>{a.lastError}</Alert>}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          {save.error && <Alert severity="error">{getErrorMessage(save.error)}</Alert>}
          {a?.connected ? (
            <Stack direction="row" spacing={1} alignItems="center"><Chip color="success" label="Connected" size="small" /><Typography variant="body2">{a.displayPhone}</Typography></Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              In Meta’s Business Manager: create an app with WhatsApp, add your business number, make a system user with a permanent token
              (whatsapp_business_messaging and whatsapp_business_management), and copy the app secret. Meta bills conversations to your account.
            </Typography>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField size="small" label="Phone number ID" value={v.phoneNumberId} onChange={(e) => setV({ ...v, phoneNumberId: e.target.value })} sx={{ flex: 1 }} />
            <TextField size="small" label="WhatsApp Business account ID" value={v.wabaId} onChange={(e) => setV({ ...v, wabaId: e.target.value })} sx={{ flex: 1 }} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField size="small" type="password" label="Access token" value={v.accessToken} onChange={(e) => setV({ ...v, accessToken: e.target.value })} sx={{ flex: 2 }}
              placeholder={a?.connected ? 'Saved; type to replace' : ''} autoComplete="off" />
            <TextField size="small" type="password" label="App secret" value={v.appSecret} onChange={(e) => setV({ ...v, appSecret: e.target.value })} sx={{ flex: 1 }}
              placeholder={a?.connected ? 'Saved; type to replace' : ''} autoComplete="off" />
            <TextField size="small" label="Country code" value={v.defaultCountryCode} onChange={(e) => setV({ ...v, defaultCountryCode: e.target.value })} sx={{ width: 120 }}
              helperText="For numbers saved without one" />
          </Stack>
          <Divider />
          <Typography variant="subtitle2">Where numbers are</Typography>
          {v.contacts.map((c, i) => (
            <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <TextField select size="small" label="Records" value={c.entityType} sx={{ flex: 1 }} onChange={(e) => setContact(i, { entityType: e.target.value, phoneField: '', nameField: '' })}>
                {entityTypes.map((t) => <MenuItem key={t} value={t}>{entityLabelPlural(t)}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Mobile field" value={c.phoneField || ''} sx={{ flex: 1 }} onChange={(e) => setContact(i, { phoneField: e.target.value })}>
                {phoneFields(c.entityType).map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Name field (new records)" value={c.nameField || ''} sx={{ flex: 1 }} onChange={(e) => setContact(i, { nameField: e.target.value || null })}>
                <MenuItem value=""><em>None</em></MenuItem>
                {nameFields(c.entityType).map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label}</MenuItem>)}
              </TextField>
              <IconButton aria-label="Remove" onClick={() => setV({ ...v, contacts: v.contacts.filter((_, j) => j !== i) })}><DeleteOutlineIcon /></IconButton>
            </Stack>
          ))}
          <Box><Button size="small" startIcon={<AddIcon />} onClick={() => setV({ ...v, contacts: [...v.contacts, { entityType: entityTypes[0] || '', phoneField: '', nameField: null }] })}>Add a record type</Button></Box>
          <TextField select size="small" label="Messages from unknown numbers" value={v.createType || ''} onChange={(e) => setV({ ...v, createType: e.target.value })} sx={{ maxWidth: 360 }}>
            <MenuItem value="">Keep them in “unmatched” below</MenuItem>
            {v.contacts.map((c) => c.entityType).filter(Boolean).map((t) => <MenuItem key={t} value={t}>Add a new {entityLabel(t).toLowerCase()}</MenuItem>)}
          </TextField>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {a?.connected && <Button color="error" disabled={disconnect.isPending} onClick={() => disconnect.mutate()}>Disconnect</Button>}
            <Button variant="contained" disabled={save.isPending || !v.phoneNumberId || !v.wabaId} onClick={() => save.mutate()}>{a?.connected ? 'Save' : 'Connect'}</Button>
          </Stack>
        </Stack>
      </Paper>
      {a?.connected && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Webhook (paste into your Meta app → WhatsApp → Configuration)</Typography>
          <Stack spacing={1.5}>
            <Copy label="Callback URL" value={a.webhookUrl} />
            <Copy label="Verify token" value={a.verifyToken} />
            <Typography variant="caption" color="text.secondary">Then subscribe to the “messages” field.</Typography>
          </Stack>
        </Paper>
      )}
      {a?.connected && <Unmatched types={(a.contacts || []).map((c) => c.entityType)} />}
    </Box>
  )
}

/** Messages from numbers no record has: put them on a record. */
const Unmatched = ({ types }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const { data = [] } = useQuery({ queryKey: [tenantId, 'whatsapp', 'unmatched'], queryFn: whatsappAPI.unmatched })
  const [search, setSearch] = useState({})
  const [options, setOptions] = useState({})
  const link = useMutation({
    mutationFn: ({ messageId, recordId }) => whatsappAPI.link(messageId, recordId),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: [tenantId, 'whatsapp'] }); notify('Linked') },
  })
  const find = async (id, q) => {
    setSearch({ ...search, [id]: q })
    if (q.trim().length < 2) return
    const pages = await Promise.all(types.map((t) => recordsAPI.list({ entityType: t, q: q.trim(), size: 5 }).catch(() => ({ items: [] }))))
    setOptions((o) => ({ ...o, [id]: pages.flatMap((p) => p.items || []) }))
  }
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Unmatched messages</Typography>
      {data.length === 0 ? <Typography variant="body2" color="text.secondary">None: every message found its record.</Typography> : data.map((m) => (
        <Stack key={m.id} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ py: 1, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{m.contactName || 'Unknown'} · +{m.waId}</Typography>
            <Typography variant="caption" color="text.secondary">{format(new Date(m.createdOn), 'd MMM HH:mm')} · {m.body}</Typography>
          </Box>
          <Autocomplete size="small" sx={{ width: 260 }} options={options[m.id] || []} getOptionLabel={(o) => o.title || ''} inputValue={search[m.id] || ''}
            onInputChange={(_, q) => find(m.id, q)} onChange={(_, o) => o && link.mutate({ messageId: m.id, recordId: o.id })}
            renderInput={(p) => <TextField {...p} placeholder="Find the record" />} />
        </Stack>
      ))}
    </Paper>
  )
}

export default WhatsAppSettingsPage
